import { TEXTOS } from '../../../../lib/glossario'
import type { ParecerDaIa } from '../../../../types/chamado'
import { rotuloDoValor } from './opcoes'
import { comentarioDe, propostaDe, type CampoProponivel } from './proposta'

interface Props {
  parecer: ParecerDaIa | null
  campo: CampoProponivel
  /** O que está marcado agora no formulário, para dizer se a sugestão foi mantida. */
  valorAtual: string | undefined
}

/**
 * O que a IA sugeriu para uma pergunta — com o porquê, a confiança e se você manteve.
 *
 * **Fica colada na pergunta, não num cartão separado.** Quando o parecer morava num cartão embaixo
 * do formulário, conferir uma resposta custava rolar a tela, achar o trecho correspondente e
 * voltar — e o fiscal acabava salvando sem ler. Aqui a sugestão, a justificativa e o campo estão
 * na mesma linha de leitura.
 *
 * **Quando a IA não sugeriu nada, isto também aparece.** Silêncio deliberado é informação: a IA
 * não propõe conformidade nem aceite porque o que ela recebe não permite — e dizer isso na tela
 * evita que a ausência de etiqueta seja lida como esquecimento do sistema.
 *
 * @param props o parecer exibido, qual campo e o valor marcado agora
 * @returns a etiqueta da sugestão, ou nada quando não há parecer
 */
export function SugestaoDaIa({ parecer, campo, valorAtual }: Props) {
  const t = TEXTOS.marcacao.sugestaoDaIa
  const comentario = comentarioDe(parecer, campo)
  if (!comentario) {
    return null
  }
  const proposta = propostaDe(parecer, campo)

  if (!proposta) {
    return (
      <p className="mt-1.5 max-w-prose text-xs text-slate-500">
        <span className="font-medium">{t.semProposta}.</span>{' '}
        {comentario.justificativa ?? ''}
      </p>
    )
  }

  const mantida = valorAtual === proposta.valorNoFormulario
  return (
    <div
      className={
        'mt-1.5 max-w-prose rounded-md border px-2.5 py-1.5 text-xs ' +
        (mantida ? 'border-niteroi-200 bg-niteroi-50/60' : 'border-slate-200 bg-slate-50')
      }
    >
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span className="font-semibold text-niteroi-800">{t.rotulo}:</span>
        <span className="text-slate-800">{rotuloDoValor(campo, proposta.valorNoFormulario)}</span>
        <span className="text-slate-500">· {t.confianca[comentario.confianca] ?? comentario.confianca}</span>
        <span className={mantida ? 'text-niteroi-700' : 'text-laranja-700'}>
          · {mantida ? t.aceita : t.alterada}
        </span>
      </p>
      {comentario.justificativa && <p className="mt-0.5 text-slate-600">{comentario.justificativa}</p>}
    </div>
  )
}
